'use client';

import React, { memo } from 'react';
import { BRAND_BLUE } from '@voley/shared-types/constants';

interface LoadingSystemProps {
  show: boolean;
  message?: string;
  fullScreen?: boolean;
  overlay?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const LoadingSystem: React.FC<LoadingSystemProps> = memo(({
  show,
  message = 'Loading...',
  fullScreen = false,
  overlay = false,
  size = 'medium'
}) => {
  // Brand color matching external pages
  const brandBlue = BRAND_BLUE;
  
  const renderLoader = () => (
    <div className="flex flex-col items-center justify-center gap-5">
      {/* Clean circular spinner with brand blue */}
      <div className="flex items-center justify-center">
        {(() => {
          const sizePxMap: Record<'small' | 'medium' | 'large', number> = { small: 20, medium: 36, large: 48 };
          const borderWidthMap: Record<'small' | 'medium' | 'large', number> = { small: 2, medium: 3, large: 4 };
          const styleObj = {
            width: sizePxMap[size],
            height: sizePxMap[size],
            borderWidth: borderWidthMap[size],
            borderStyle: 'solid',
            borderColor: `${brandBlue}20`, // Light blue track
            borderTopColor: brandBlue,      // Brand blue spinner
            borderRadius: '50%',
          } as React.CSSProperties;

          return (
            <div
              style={styleObj}
              className="animate-spin"
            />
          );
        })()}
      </div>
      
      {/* Clean message with system font */}
      {message && (
        <div className="text-center">
          <p
            className="animate-fade-in"
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              color: 'var(--color-text-secondary)',
              fontWeight: 400,
              fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1rem' : '0.9375rem',
              letterSpacing: '-0.01em',
            }}
          >
            {message}
          </p>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    if (!show) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
        data-testid="loading-spinner"
      >
        {renderLoader()}
      </div>
    );
  }

  if (overlay) {
    if (!show) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
        style={{ backgroundColor: 'color-mix(in srgb, var(--color-background) 90%, transparent)' }}
      >
        {renderLoader()}
      </div>
    );
  }

  // Inline loading
  if (!show) return null;
  return (
    <div className="flex items-center justify-center p-4">
      {renderLoader()}
    </div>
  );
});

LoadingSystem.displayName = 'LoadingSystem';

export default LoadingSystem;

// Export a simple inline loader for buttons and small areas
export const InlineLoader: React.FC<{ size?: 'xs' | 'sm' | 'md' }> = memo(({ size = 'sm' }) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5'
  };

  return (
    <div
      className={`${sizeClasses[size]} relative animate-spin`}
    >
      <div className="absolute inset-0 rounded-full border-2 border-current opacity-20" />
      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-current" />
    </div>
  );
});

InlineLoader.displayName = 'InlineLoader';

// Export skeleton loaders for content
export const SkeletonLoader: React.FC<{ 
  className?: string;
  count?: number;
  height?: string;
}> = memo(({ className = '', count = 1, height = 'h-4' }) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`rounded ${height} w-full animate-skeleton-pulse`}
          style={{
            backgroundColor: 'var(--color-primary)',
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
});

SkeletonLoader.displayName = 'SkeletonLoader';

// Basic skeleton component that respects dark mode
// Uses primary brand color with opacity for visibility
function Skeleton({ 
  className = '', 
  ...props 
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={`animate-pulse rounded ${className}`}
      style={{
        backgroundColor: 'var(--color-primary)',
        opacity: 0.1
      }}
      {...props}
    />
  );
}

// Message skeleton for chat lists
export function MessageSkeleton({ isIncoming = true }: { isIncoming?: boolean }) {
  return (
    <div className="p-3 flex items-start gap-3">
      <Skeleton className="w-8 h-8 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <div 
          className="rounded-lg p-3"
          style={{ 
            backgroundColor: isIncoming 
              ? 'var(--color-card-bg)' 
              : 'var(--color-primary)',
            opacity: isIncoming ? 0.1 : 0.15
          }}
        >
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    </div>
  );
}

// Contact skeleton for contact lists
// Matches ConversationRowItem layout
export function ContactSkeleton() {
  return (
    <div 
      className="px-4 py-3 transition-colors flex items-start gap-3"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
        <Skeleton className="w-10 h-10 rounded-full shrink-0" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-12 shrink-0" />
        </div>
        <Skeleton className="h-3 w-full max-w-[70%]" />
      </div>
    </div>
  );
}

// Conversation skeleton for conversation lists
export function ConversationSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: count }, (_, i) => (
        <ContactSkeleton key={i} />
      ))}
    </div>
  );
}

// Stats card skeleton
export function StatsCardSkeleton() {
  return (
    <div 
      className="p-6 rounded-lg border shadow-sm"
      style={{ 
        backgroundColor: 'var(--color-card-bg)',
        borderColor: 'var(--color-border)'
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-6 rounded" />
      </div>
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

// Dashboard skeleton for dashboard pages
export function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div 
        className="border-b pb-4"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      
      {/* Stats grid skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }, (_, i) => (
          <StatsCardSkeleton key={i} />
        ))}
      </div>
      
      {/* Content area skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div 
          className="p-6 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-border)'
          }}
        >
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div 
          className="p-6 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-border)'
          }}
        >
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="h-48">
            <Skeleton className="h-full w-full rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Table skeleton for data tables
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div 
      className="rounded-lg shadow border overflow-hidden"
      style={{ 
        backgroundColor: 'var(--color-card-bg)',
        borderColor: 'var(--color-border)'
      }}
    >
      {/* Table header */}
      <div 
        className="px-6 py-3 border-b"
        style={{ 
          backgroundColor: 'var(--color-card-header-bg)',
          borderColor: 'var(--color-border)'
        }}
      >
        <div className="flex gap-4">
          {Array.from({ length: cols }, (_, i) => (
            <Skeleton key={i} className="h-4 flex-1" />
          ))}
        </div>
      </div>
      
      {/* Table rows */}
      {Array.from({ length: rows }, (_, i) => (
        <div 
          key={i} 
          className="px-6 py-4 border-b last:border-b-0"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div className="flex gap-4">
            {Array.from({ length: cols }, (_, j) => (
              <Skeleton key={j} className="h-4 flex-1" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Form skeleton for forms
export function FormSkeleton() {
  return (
    <div 
      className="p-6 rounded-lg border"
      style={{ 
        backgroundColor: 'var(--color-card-bg)',
        borderColor: 'var(--color-border)'
      }}
    >
      <div className="space-y-6">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}
        
        <div className="flex gap-3 pt-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-20 rounded-md" />
        </div>
      </div>
    </div>
  );
}

// ContentSkeleton for DataStateWrapper
export function ContentSkeleton({ 
  type = 'list', 
  count = 5, 
  className = '' 
}: { 
  type?: 'list' | 'grid' | 'table' | 'card' | 'profile';
  count?: number;
  className?: string;
}) {
  switch (type) {
    case 'list':
      return <ConversationSkeleton count={count} />;
    case 'table':
      return <TableSkeleton rows={count} />;
    case 'grid':
      return (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
          {Array.from({ length: count }, (_, i) => (
            <StatsCardSkeleton key={i} />
          ))}
        </div>
      );
    case 'card':
      return <StatsCardSkeleton />;
    case 'profile':
      return <FormSkeleton />;
    default:
      return <SkeletonLoader count={count} className={className} />;
  }
}
